import React, { useMemo } from 'react';
import { Box, CategoryIcon, costFormater, Text, Tip, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import ActivityStatus from '@/modules/transaction/components/ActivityStatus';
import getActivityTypeColor from '@/utils/whiteLabeling/getActivityTypeColor';

import { Transaction, TransactionStatus } from '../../transaction.types';

import { Date, Wrapper } from './RecentActivity.styles';

const MAX_PROVIDER_LENGTH = 45;

interface Props {
  data: Transaction;
  hasBacklight?: boolean;
  onPick?: () => void;
}

const RecentActivity: React.FC<Props> = ({ data, hasBacklight, onPick }) => {
  const provider = useMemo(() => data?.provider || '', [data]);
  const background = useMemo(() => getActivityTypeColor(data?.categoryType) as string, [data]);
  const { isMobile } = useResponsive();

  const isPending = useMemo(() => {
    return data.status === TransactionStatus.Pending || data.status === TransactionStatus.Processing;
  }, [data.status]);
  if (isMobile) {
    return (
      <Wrapper data-testid="RecentActivity-wrapper" hasBacklight={hasBacklight} onClick={onPick}>
        <Box style={{ flex: 2 }} direction="row" align="center">
          <Box width={{ min: 'max-content' }}>
            <CategoryIcon size="m" category={data.categoryType} {...(background ? { background } : {})} />
          </Box>
          <Box direction="column" margin={{ left: 'spacing12' }}>
            <Tip
              plain
              content={
                <Box
                  width="max-content"
                  align="center"
                  justify="center"
                  background="background3"
                  border={{ size: 'small', color: 'popup' }}
                >
                  <Text size="small" color="textOnColor">
                    {provider}
                  </Text>
                </Box>
              }
            >
              <Text size="medium" alignSelf="start" truncate style={{ lineHeight: 1.2 }}>
                {provider}
              </Text>
            </Tip>
            <Text
              alignSelf="start"
              size="small"
              weight={500}
              margin={{ top: 'spacing2' }}
              color="textSecondary"
            >
              {dayjs(data?.statusDate).format(DEFAULT_DATE_FORMAT)}
            </Text>
          </Box>
        </Box>

        <Box style={{ flex: 1 }} direction="row" justify="end" align="center">
          <Text size="large" weight={700} alignSelf="end" wordBreak="break-word">
            {isPending ? costFormater(data?.requestedAmount, true) : costFormater(data.paidAmount, true)}
          </Text>
          {isPending && (
            <Box margin={{ left: 'spacing8' }}>
              <ActivityStatus isMinimizedMode status={data?.status} />
            </Box>
          )}
        </Box>
      </Wrapper>
    );
  }
  return (
    <Wrapper data-testid="RecentActivity-wrapper" hasBacklight={hasBacklight} onClick={onPick}>
      <Box style={{ flex: 1.5 }} direction="row">
        <Box width={{ min: 'max-content' }}>
          <CategoryIcon size="s" category={data.categoryType} {...(background ? { background } : {})} />
        </Box>
        <Tip
          plain
          dropProps={{ align: { left: 'right' } }}
          content={
            provider.length > MAX_PROVIDER_LENGTH ? (
              <Box
                width="max-content"
                height={{ max: 'l' }}
                align="center"
                justify="center"
                background="background3"
                border={{ size: 'small', color: 'popup' }}
                pad={{ horizontal: 'spacing6' }}
              >
                <Text size="small" color="textOnColor">
                  {provider}
                </Text>
              </Box>
            ) : undefined
          }
        >
          <Text
            size="medium"
            alignSelf="start"
            margin={{ right: 'spacing24', left: 'spacing8', vertical: 'auto' }}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {provider}
          </Text>
        </Tip>
      </Box>

      <Box style={{ flex: 1 }}>
        <Text size="medium" alignSelf="start" margin={{ right: 'spacing24' }} wordBreak="break-word">
          {costFormater(data?.requestedAmount, true)}
        </Text>
      </Box>

      <Box style={{ flex: 0.8 }}>
        <Text size="medium" alignSelf="start" margin={{ right: 'spacing24' }} wordBreak="break-word">
          {costFormater(data.paidAmount, true)}
        </Text>
      </Box>
      <Box style={{ flex: 1.2, minWidth: '170px' }}>
        <ActivityStatus status={data?.status} />
      </Box>
      <Box data-testid="StatusDate-wrapper">
        <Date>{dayjs(data?.statusDate).format(DEFAULT_DATE_FORMAT)}</Date>
      </Box>
    </Wrapper>
  );
};

export default RecentActivity;
